/* Noche azul: progressive enhancement driven only by native scroll. */
(() => {
  'use strict';
  if (!window.customElements || customElements.get('sauve-scroll-story')) return;

  const clamp = (value) => Math.max(0, Math.min(1, value));
  const smooth = (value) => {
    const t = clamp(value);
    return t * t * (3 - 2 * t);
  };

  class SauveScrollStory extends HTMLElement {
    connectedCallback() {
      if (this._controller) return;
      this._controller = new AbortController();
      const { signal } = this._controller;
      // A theme-editor insertion may connect before its children are parsed.
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this._start(), { once: true, signal });
      } else {
        this._start();
      }
    }

    _start() {
      if (!this.isConnected || this._started) return;
      this._track = this.querySelector('.sv-scroll__track');
      this._stage = this._track?.querySelector('.sv-scroll__stage');
      this._image = this._stage?.querySelector('img.sv-scroll__scene, .sv-scroll__scene img');
      if (!this._track || !this._stage || !this._image) return;
      this._started = true;
      this._chapters = [...this._stage.querySelectorAll('.sv-scroll__chapter[data-chapter]')];
      this._steps = [...this._stage.querySelectorAll('.sv-scroll__step')];
      this._bar = this._stage.querySelector('.sv-scroll__bar span');
      this._reduce = matchMedia('(prefers-reduced-motion: reduce)');
      this._imageReady = false;
      this._visible = true;
      this._measureNeeded = true;
      this._frame = 0;
      this._viewportHeight = 0;
      this._viewportWidth = 0;
      const { signal } = this._controller;
      const schedule = () => this._schedule();
      const measure = () => {
        this._measureNeeded = true;
        this._schedule(true);
      };
      window.addEventListener('scroll', schedule, { passive: true, signal });
      window.addEventListener('resize', measure, { passive: true, signal });
      window.addEventListener('pageshow', measure, { signal });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          cancelAnimationFrame(this._frame);
          this._frame = 0;
        } else measure();
      }, { signal });
      if (this._reduce.addEventListener) {
        this._reduce.addEventListener('change', measure, { signal });
      } else {
        this._reduce.addListener(measure);
        signal.addEventListener('abort', () => this._reduce.removeListener(measure), { once: true });
      }

      this._probe = document.createElement('div');
      this._probe.setAttribute('aria-hidden', 'true');
      this._probe.style.cssText = 'position:absolute;top:0;left:0;visibility:hidden;pointer-events:none;width:0;height:100svh;overflow:hidden;';
      this.append(this._probe);
      if ('ResizeObserver' in window) {
        this._resizeObserver = new ResizeObserver(measure);
      }
      this._refreshChrome();
      // Shopify injects/replaces its preview toolbar after the section connects.
      this._chromeObserver = new MutationObserver(() => {
        if (this._refreshChrome()) measure();
      });
      this._chromeObserver.observe(document.body, { childList: true, subtree: true });
      this._barObserver = new MutationObserver(measure);
      if (this._previewBar) this._observeBar();
      if ('IntersectionObserver' in window) {
        this._intersectionObserver = new IntersectionObserver(([entry]) => {
          this._visible = entry.isIntersecting;
          // Paint the boundary once on exit; do not run frames offscreen.
          this._schedule(true);
        });
        this._intersectionObserver.observe(this._track);
      }

      const decode = async () => {
        const revision = this._decodeRevision = (this._decodeRevision || 0) + 1;
        this._imageReady = false;
        measure();
        try {
          if (!this._image.complete || !this._image.naturalWidth) return;
          if (typeof this._image.decode === 'function') await this._image.decode();
          if (signal.aborted || revision !== this._decodeRevision) return;
          this._imageReady = !!this._image.naturalWidth;
          this.dataset.imageState = this._imageReady ? 'ready' : 'error';
        } catch (_) {
          if (signal.aborted || revision !== this._decodeRevision) return;
          this.dataset.imageState = 'error';
        }
        if (!signal.aborted) measure();
      };
      this._image.addEventListener('load', decode, { signal });
      this._image.addEventListener('error', () => {
        this._decodeRevision = (this._decodeRevision || 0) + 1;
        this._imageReady = false;
        this.dataset.imageState = 'error';
        measure();
      }, { signal });
      this.dataset.imageState = 'loading';
      if (this._image.complete) {
        if (this._image.naturalWidth) decode();
        else this.dataset.imageState = 'error';
      }
      this._paint(0);
      measure();
    }

    _refreshChrome() {
      const header = document.querySelector('.header-section');
      const bar = document.querySelector('#PBarNextFrameWrapper');
      if (header === this._header && bar === this._previewBar) return false;
      if (this._header) this._resizeObserver?.unobserve(this._header);
      if (this._previewBar) this._resizeObserver?.unobserve(this._previewBar);
      this._header = header;
      this._previewBar = bar;
      if (header) this._resizeObserver?.observe(header);
      if (bar) this._resizeObserver?.observe(bar);
      this._observeBar();
      return true;
    }

    _observeBar() {
      this._barObserver?.disconnect();
      if (this._previewBar && this._barObserver) {
        this._barObserver.observe(this._previewBar, {
          attributes: true, attributeFilter: ['style', 'class', 'hidden'],
        });
      }
    }

    _measure() {
      this._measureNeeded = false;
      const width = document.documentElement.clientWidth;
      const hasSmallViewport = window.CSS?.supports('height', '100svh');
      // svh stays stable as mobile browser chrome expands/collapses. On older
      // mobile engines retain height on height-only resizes to avoid pin jumps.
      if (hasSmallViewport || width >= 750 || width !== this._viewportWidth || !this._viewportHeight) {
        this._viewportHeight = hasSmallViewport
          ? this._probe.getBoundingClientRect().height
          : window.innerHeight;
      }
      this._viewportWidth = width;
      const viewport = this._viewportHeight || window.innerHeight;
      this._headerHeight = Math.max(0, this._header?.getBoundingClientRect().height || 0);
      let toolbar = 0;
      if (this._previewBar) {
        const rect = this._previewBar.getBoundingClientRect();
        const style = getComputedStyle(this._previewBar);
        if (style.display !== 'none' && style.visibility !== 'hidden' && Number(style.opacity) !== 0 && rect.width > 0) {
          toolbar = Math.max(0, Math.min(window.innerHeight, rect.bottom) - Math.max(0, rect.top));
        }
      }
      // Keep the announcement/header space from the initial document flow,
      // rather than making the stage taller as the announcement scrolls away.
      const initialTop = Math.max(0, this._track.getBoundingClientRect().top + window.scrollY);
      const topSpace = Math.max(this._headerHeight, Math.min(initialTop, viewport / 2));
      this.style.setProperty('--sv-scroll-header', `${this._headerHeight.toFixed(2)}px`);
      const availableHeight = Math.max(1, viewport - topSpace - toolbar);
      this.style.setProperty('--sv-scroll-height', `${availableHeight.toFixed(2)}px`);
      const minimumHeight = parseFloat(getComputedStyle(this._stage).minHeight) || 0;
      const enhanced = this._imageReady && !this._reduce.matches && viewport >= 620 && availableHeight >= minimumHeight;
      this.classList.toggle('is-enhanced', enhanced);
      this.dataset.motion = enhanced ? 'native-scroll' : 'static';
      this._enhanced = enhanced;
    }

    _schedule(force = false) {
      if (!this._started || document.hidden || (!force && !this._visible) || this._frame) return;
      this._frame = requestAnimationFrame(() => {
        this._frame = 0;
        if (!this.isConnected || document.hidden) return;
        if (this._measureNeeded) this._measure();
        const range = this._track.offsetHeight - this._stage.offsetHeight;
        const progress = this._enhanced && range > 0
          ? clamp((this._headerHeight - this._track.getBoundingClientRect().top) / range)
          : 0;
        this._paint(progress);
      });
    }

    _paint(progress) {
      const q = smooth(progress);
      const mobile = this._viewportWidth < 750;
      const stageHeight = this._stage.offsetHeight;
      const x = mobile ? -28 * q : -140 * q * this._viewportWidth / 1440;
      const y = q * (mobile ? -53 * stageHeight / 844 : 34 * stageHeight / 900);
      this.style.setProperty('--sv-scroll-p', progress.toFixed(5));
      this.style.setProperty('--sv-scroll-x', `${x.toFixed(3)}px`);
      this.style.setProperty('--sv-scroll-y', `${y.toFixed(3)}px`);
      this.style.setProperty('--sv-scroll-scale', (1 + (mobile ? 0.24 : 0.26) * q).toFixed(5));
      const firstFade = smooth(progress / 0.28);
      const lastFade = smooth((progress - 0.58) / 0.22);
      const weights = [1 - firstFade, firstFade * (1 - lastFade), lastFade];
      const active = weights.indexOf(Math.max(...weights));
      this._chapters.forEach((chapter) => {
        const index = Number(chapter.dataset.chapter);
        const weight = weights[index] || 0;
        chapter.style.opacity = weight.toFixed(5);
        chapter.style.transform = `translateY(${((1 - weight) * 18).toFixed(3)}px)`;
        chapter.setAttribute('aria-hidden', index === active ? 'false' : 'true');
        chapter.toggleAttribute('inert', index !== active);
      });
      this._steps.forEach((step, index) => {
        step.classList.toggle('is-active', index === active);
        step.classList.toggle('active', index === active);
      });
      // CSS owns the progress bar transform; do not multiply progress by width.
      if (this._bar) this._bar.style.width = '100%';
      this.dataset.progress = progress.toFixed(5);
      this.dataset.chapter = String(active);
    }

    disconnectedCallback() {
      this._controller?.abort();
      this._controller = null;
      cancelAnimationFrame(this._frame);
      this._frame = 0;
      this._resizeObserver?.disconnect();
      this._intersectionObserver?.disconnect();
      this._chromeObserver?.disconnect();
      this._barObserver?.disconnect();
      this._probe?.remove();
      this._header = null;
      this._previewBar = null;
      this.classList.remove('is-enhanced');
      if (this._started) this._paint(0);
      this._started = false;
    }
  }

  customElements.define('sauve-scroll-story', SauveScrollStory);
})();
